import { format, isDate, isValid } from "date-fns";
import { Enum, NamedEnum, enumByIdentifier, EnumIdentifier, equalsToEnum } from "./enums";
import assert from "assert";
import { hasTimePart, isFunction, isIntegerRange, isNumber, isPercentageRange, isString, unexpectedCodePath } from "./misc";
import { Id } from "../Id";
import { dateOnly_FROM_date } from "../data-helpers";
import { formatInTimeZone } from "date-fns-tz";

// #region TYPES

export type Text       = string & { __text__:       void };
export type DateOnly   = Date   & { __dateOnly__:   void };
export type DateTime   = Date   & { __dateTime__:   void };
export type Int        = number & { __int__:        void };
export type Decimal    = number & { __decimal__:    void };
export type Money      = number & { __money__:      void };
export type Percentage = number & { __percentage__: void };

export const text = (_: string): Text => {
  assert(isText(_));
  return _;
}
export const dateOnly = (_: Date): DateOnly => {
  assert(isDateOnly(_));
  return _;
}
export const dateTime = (_: Date): DateTime => {
  assert(isDateTime(_));
  return _;
}
export const int = (_: number): Int => {
  assert(isInt(_));
  return _;
}
export const decimal = (fractionDigits: number) => {
  assert(isFractionDigits(fractionDigits));
  return (_: number): Decimal => {
    assert(isDecimal(fractionDigits)(_));
    return _;
  }
}
export const money = (_: number): Money => {
  assert(isMoney(_));
  return _;
}
export const percentage = (_: number): Percentage => {
  assert(isPercentage(_));
  return _;
}

export const optionalText = (_: string | undefined): Text | undefined => {
  assert(_ === undefined || isText(_));
  return _ === undefined ? undefined : text(_);
}
export const optionalDateOnly = (_: Date | undefined): DateOnly | undefined => {
  assert(_ === undefined || isDateOnly(_), `Invalid value: ${_}`);
  return _ === undefined ? undefined : dateOnly(_);
}
export const optionalDateTime = (_: Date | undefined): DateTime | undefined => {
  assert(_ === undefined || isDateTime(_));
  return _ === undefined ? undefined : dateTime(_);
}
export const optionalInt = (_: number | undefined): Int | undefined => {
  assert(_ === undefined || isInt(_));
  return _ === undefined ? undefined : int(_);
}
export const optionalDecimal = (fractionDigits: number) => {
  assert(isFractionDigits(fractionDigits));
  return (_: number | undefined): Decimal | undefined => {
    assert(_ === undefined || isDecimal(fractionDigits)(_));
    return _ === undefined ? undefined : decimal(fractionDigits)(_);
  }
}
export const optionalMoney = (_: number | undefined): Money | undefined => {
  assert(_ === undefined || isMoney(_));
  return _ === undefined ? undefined : money(_);
}
export const optionalPercentage = (_: number | undefined): Percentage | undefined => {
  assert(_ === undefined || isPercentage(_));
  return _ === undefined ? undefined : percentage(_);
}

// #endregion
// #region IS

const isText       = (_: any): _ is Text       => isString(_) && _.trim() === _ && _ !== "";
const isDateOnly   = (_: any): _ is DateOnly   => isDate(_) && isValid(_) && !hasTimePart(_);
const isDateTime   = (_: any): _ is DateTime   => isDate(_) && isValid(_);
const isInt        = (_: any): _ is Int        => isNumber(_) && Number.isInteger(_) && isIntegerRange(_);
const isMoney      = (_: any): _ is Money      => isDecimal(2)(_);
const isPercentage = (_: any): _ is Percentage => isNumber(_) && Number.isInteger(_) && isPercentageRange(_);

const isDecimal = (fractionDigits: number) => {
  assert(isFractionDigits(fractionDigits));
  return (_: any): _ is Decimal => isNumber(_) && hasFractionDigits(fractionDigits)(_);
}
const isEnum = <TEnum extends Enum>(enums: TEnum[]) => {
  assert(isEnums(enums));
  return (_: any): _ is TEnum => enums.includes(_);
}
const isEnumIdentifier = <TEnum extends Enum>(enums: TEnum[]) => {
  assert(isEnums(enums));
  return (_: any): _ is string => isString(_) && enums.some(equalsToEnum(_));
}
const isId = Id.is;

export const IS = {
  text:           isText,
  dateOnly:       isDateOnly,
  dateTime:       isDateTime,
  int:            isInt,
  decimal:        isDecimal,
  money:          isMoney,
  enum:           isEnum,
  enumIdentifier: isEnumIdentifier,
  id:             isId,
  percentage:     isPercentage,
}

// #endregion

// #region Editable

export type Editable = string & { __editable__: void }

export const editable = (_: string = ""): Editable => {
  assert(isEditable(_));
  return _;
}

export const isEditable      = (_: any): _ is Editable => typeof _ === "string";
export const isEmptyEditable = (_: any)                => isEditable(_) && _.trim() === "";

// #endregion
// #region editable FROM

export const formatDateOnly = (_: Date) => formatInTimeZone(_, 'UTC', "M/d/y");
export const formatDateTime = (_: Date) => format(_, "M/d/y h:mm a");

export const formatInt        =                             (_: number) =>           addCommas_int    (_.toFixed(0             ));
export const formatDecimal    = (fractionDigits: number) => (_: number) =>           addCommas_decimal(_.toFixed(fractionDigits));
export const formatMoney      =                             (_: number) => addDollar(addCommas_decimal(_.toFixed(2             )));
export const formatPercentage =                             (_: number) =>           addPercent       (_.toFixed(0             ));


export const formatBoolean  =                             (_: boolean) => _ ? "Yes" : "No";

export const formatArray    = (_?: string[]) => _ ? [...new Set(_)].join(", ") : "";

const addCommas_int      = (_: string) => _.replace(/(\d)(?=(?:\d{3})+$)/g,  "$1,");
const addCommas_decimal  = (_: string) => _.replace(/(\d)(?=(?:\d{3})+\.)/g, "$1,");
const addDollar  = (_: string) => `$${_}`;
const addPercent = (_: string) => `${_}%`;

const editable_FROM_text = (_: Text): Editable => {
  assert(isText(_));
  return editable(_);
}
const editable_FROM_dateOnly = (_: DateOnly): Editable => {
  assert(isDateOnly(_));
  return editable(formatDateOnly(_));
}
const editable_FROM_dateTime = (_: DateTime): Editable => {
  assert(isDateTime(_));
  return editable(formatDateTime(_));
}
const editable_FROM_int = (_: Int): Editable => {
  assert(isInt(_));
  return editable(formatInt(_));
}
const editable_FROM_decimal = (fractionDigits: number) => {
  assert(isFractionDigits(fractionDigits));
  return (_: Decimal ): Editable => {
    assert(isDecimal(fractionDigits)(_));
    return editable(formatDecimal(fractionDigits)(_));
  }
}
const editable_FROM_money = (_: Money): Editable => {
  assert(isMoney(_));
  return editable(formatMoney(_));
}
const editable_FROM_enum = <TEnum extends Enum>(enums: TEnum[]) => {
  assert(isEnums(enums));
  return (_: TEnum): Editable => {
    assert(isEnum(enums)(_));
    return editable(_.identifier);
  }
}
const editable_FROM_enumName = <TEnum extends NamedEnum>(enums: TEnum[]) => {
  assert(isEnums(enums));
  return (_: TEnum): Editable => {
    assert(isEnum(enums)(_));
    return editable(_.name);
  }
}
const editable_FROM_enumIdentifier = <TEnum extends Enum>(enums: TEnum[]) => {
  assert(isEnums(enums));
  return (_: EnumIdentifier): Editable => {
    assert(isEnumIdentifier(enums)(_));
    return editable(_);
  }
}
const editable_FROM_percentage = (_: Percentage): Editable => {
  assert(isPercentage(_));
  return editable(formatPercentage(_));
}
const editable_FROM_id = (_: Id): Editable => {
  assert(isId(_));
  return editable(_.asInt.toString());
}

export const editable_FROM = {
  text:           editable_FROM_text,
  dateOnly:       editable_FROM_dateOnly,
  dateTime:       editable_FROM_dateTime,
  int:            editable_FROM_int,
  decimal:        editable_FROM_decimal,
  money:          editable_FROM_money,
  enum:           editable_FROM_enum,
  enumIdentifier: editable_FROM_enumIdentifier,
  enumName:       editable_FROM_enumName,
  percentage:     editable_FROM_percentage,
  id:             editable_FROM_id,
}

// #endregion
// #region editable FROM OPTIONAL

const editable_FROM_OPTIONAL_text = (_: Text | undefined): Editable => {
  assert(_ === undefined || isText(_));
  return _ === undefined ? editable() : editable_FROM.text(_);
}
const editable_FROM_OPTIONAL_dateOnly = (_: DateOnly | undefined): Editable => {
  assert(_ === undefined || isDateOnly(_));
  return _ === undefined ? editable() : editable_FROM.dateOnly(_);
}
const editable_FROM_OPTIONAL_dateTime = (_: DateTime | undefined): Editable => {
  assert(_ === undefined || isDateTime(_));
  return _ === undefined ? editable() : editable_FROM.dateTime(_);
}
const editable_FROM_OPTIONAL_int = (_: Int | undefined): Editable => {
  assert(_ === undefined || isInt(_));
  return _ === undefined ? editable() : editable_FROM.int(_);
}
const editable_FROM_OPTIONAL_decimal = (fractionDigits: number) => {
  assert(isFractionDigits(fractionDigits));
  return (_: Decimal | undefined): Editable => {
    assert(_ === undefined || isDecimal(fractionDigits)(_));
    return _ === undefined ? editable() : editable_FROM.decimal(fractionDigits)(_);
  }
}
const editable_FROM_OPTIONAL_money = (_: Money | undefined): Editable => {
  assert(_ === undefined || isMoney(_));
  return _ === undefined ? editable() : editable_FROM.money(_);
}
const editable_FROM_OPTIONAL_enum = <TEnum extends Enum>(enums: TEnum[]) => {
  assert(isEnums(enums));
  return (_: TEnum | undefined): Editable => {
    assert(_ === undefined || isEnum(enums)(_));
    return _ === undefined ? editable() : editable_FROM.enum(enums)(_);
  }
}
const editable_FROM_OPTIONAL_enumIdentifier = <TEnum extends Enum>(enums: TEnum[]) => {
  assert(isEnums(enums));
  return (_: EnumIdentifier | undefined): Editable => {
    assert(_ === undefined || isEnumIdentifier(enums)(_));
    return _ === undefined ? editable() : editable_FROM.enumIdentifier(enums)(_);
  }
}
const editable_FROM_OPTIONAL_percentage = (_: Percentage | undefined): Editable => {
  assert(_ === undefined || isPercentage(_));
  return _ === undefined ? editable() : editable_FROM.percentage(_);
}
const editable_FROM_OPTIONAL_id = (_: Id | undefined): Editable => {
  assert(_ === undefined || isId(_));
  return _ === undefined ? editable() : editable_FROM.id(_);
}

export const editable_FROM_OPTIONAL = {
  text:           editable_FROM_OPTIONAL_text,
  dateOnly:       editable_FROM_OPTIONAL_dateOnly,
  dateTime:       editable_FROM_OPTIONAL_dateTime,
  int:            editable_FROM_OPTIONAL_int,
  decimal:        editable_FROM_OPTIONAL_decimal,
  money:          editable_FROM_OPTIONAL_money,
  enum:           editable_FROM_OPTIONAL_enum,
  enumIdentifier: editable_FROM_OPTIONAL_enumIdentifier,
  percentage:     editable_FROM_OPTIONAL_percentage,
  id:             editable_FROM_OPTIONAL_id,
}

// #endregion
// #region editable TRY_TO

// const DATE_ONLY_FORMAT  = /^[0-9]{1,2}\/[0-9]{1,2}\/[0-9]{2}(?:[0-9]{2})?$/;
const DATE_ONLY_FORMAT = /^[0-9]{1,2}\/[0-9]{1,2}\/[0-9]{4}$/;
const DATE_TIME_FORMAT = /^[0-9]{1,2}\/[0-9]{1,2}\/[0-9]{2}(?:[0-9]{2})? [0-9]{1,2}:[0-9]{1,2}(?::[0-9]{1,2}(?:\.[0-9]{1,3})?)? [AP]M$/

const stripCommas_int     = (_: string) => _.replace(/,/g, "");
const stripCommas_decimal = (_: string) => _.replace(/,(?=(?:[\d,]*)*\.)/g, "");
const stripDollar         = (_: string) => _.replace(/^\$/, "");
const stripPercent        = (_: string) => _.replace(/%$/, "");

const editable_TRY_TO_text = (_: Editable): Text | undefined => {
  assert(isEditable(_));
  assert(!isEmptyEditable(_));
  const candidate = _.trim();
  return IS.text(candidate) ? candidate : undefined;
}
const editable_TRY_TO_dateOnly = (_: Editable): DateOnly | undefined => {
  assert(isEditable(_));
  assert(!isEmptyEditable(_));

  const trimmed = _.trim();
  if (!DATE_ONLY_FORMAT.test(trimmed)) return undefined;

  const candidate = tryDateOnly(trimmed);
  return IS.dateOnly(candidate) ? candidate : undefined;
}
const editable_TRY_TO_dateTime = (_: Editable): DateTime | undefined => {
  assert(isEditable(_));
  assert(!isEmptyEditable(_));

  const trimmed = _.trim();
  if (!DATE_TIME_FORMAT.test(trimmed)) return undefined;

  const candidate = tryDate(trimmed);
  return IS.dateTime(candidate) ? candidate : undefined;
}
const editable_TRY_TO_int = (_: Editable): Int | undefined => {
  assert(isEditable(_));
  assert(!isEmptyEditable(_));
  const cleaned = stripCommas_int(_.trim())
  const candidate = tryNumber(cleaned);
  return IS.int(candidate) ? candidate : undefined;
}
const editable_TRY_TO_decimal = (fractionDigits: number) => {
  assert(isFractionDigits(fractionDigits));
  return (_: Editable): Decimal | undefined => {
    assert(isEditable(_));
    assert(!isEmptyEditable(_));
    const cleaned = stripCommas_decimal(_.trim())
    const candidate = tryNumber(cleaned);
    return IS.decimal(fractionDigits)(candidate) ? candidate : undefined;
  }
}
const editable_TRY_TO_money = (_: Editable): Money | undefined => {
  assert(isEditable(_));
  assert(!isEmptyEditable(_));
  const cleaned = stripDollar(stripCommas_decimal(_.trim()))
  const candidate = tryNumber(cleaned);
  return IS.money(candidate) ? candidate : undefined;
}
const editable_TRY_TO_enum = <TEnum extends Enum>(enums: TEnum[]) => {
  assert(isEnums(enums));
  return (_: Editable): TEnum | undefined => {
    assert(isEditable(_));
    assert(!isEmptyEditable(_));
    const candidateIdentifier = _.trim();
    return IS.enumIdentifier(enums)(candidateIdentifier) ? enumByIdentifier(enums)(candidateIdentifier) : undefined;
  }
}
const editable_TRY_TO_enumIdentifier = <TEnum extends Enum>(enums: TEnum[]) => {
  assert(isEnums(enums));
  return (_: Editable): string | undefined => {
    assert(isEditable(_));
    assert(!isEmptyEditable(_));
    const candidateIdentifier = _.trim();
    return IS.enumIdentifier(enums)(candidateIdentifier) ? candidateIdentifier : undefined;
  }
}
const editable_TRY_TO_id = (_: Editable): Id | undefined => {
  assert(isEditable(_));
  assert(!isEmptyEditable(_));
  return IS.id(_) ? Id.from(_) : undefined;
}
const editable_TRY_TO_percentage = (_: Editable): Percentage | undefined => {
  assert(isEditable(_));
  assert(!isEmptyEditable(_));
  const cleaned = stripPercent(_.trim())
  const candidate = tryNumber(cleaned);
  return IS.percentage(candidate) ? candidate : undefined;
}

export const editable_TRY_TO = {
  text:           editable_TRY_TO_text,
  dateOnly:       editable_TRY_TO_dateOnly,
  dateTime:       editable_TRY_TO_dateTime,
  int:            editable_TRY_TO_int,
  decimal:        editable_TRY_TO_decimal,
  money:          editable_TRY_TO_money,
  enum:           editable_TRY_TO_enum,
  enumIdentifier: editable_TRY_TO_enumIdentifier,
  id:             editable_TRY_TO_id,
  percentage:     editable_TRY_TO_percentage,
}

// #endregion
// #region editable IS

const editable_IS_text = (_: Editable) => {
  assert(isEditable(_));
  assert(!isEmptyEditable(_));
  return editable_TRY_TO.text(_) !== undefined;
}
const editable_IS_dateOnly = (_: Editable) => {
  assert(isEditable(_));
  assert(!isEmptyEditable(_));
  return editable_TRY_TO.dateOnly(_) !== undefined;
}
const editable_IS_dateTime = (_: Editable) => {
  assert(isEditable(_));
  assert(!isEmptyEditable(_));
  return editable_TRY_TO.dateTime(_) !== undefined;
}
const editable_IS_int = (_: Editable) => {
  assert(isEditable(_));
  assert(!isEmptyEditable(_));
  return editable_TRY_TO.int(_) !== undefined;
}
const editable_IS_decimal = (fractionDigits: number) => {
  assert(isFractionDigits(fractionDigits));
  return (_: Editable) => {
    assert(isEditable(_));
    assert(!isEmptyEditable(_));
    return editable_TRY_TO.decimal(fractionDigits)(_) !== undefined;
  }
}
const editable_IS_money = (_: Editable) => {
  assert(isEditable(_));
  assert(!isEmptyEditable(_));
  return editable_TRY_TO.money(_) !== undefined;
}
const editable_IS_enum = <TEnum extends Enum>(enums: TEnum[]) => {
  assert(isEnums(enums));
  return (_: Editable) => {
    assert(isEditable(_));
    assert(!isEmptyEditable(_));
    return editable_TRY_TO.enum(enums)(_) !== undefined;
  }
}
const editable_IS_enumIdentifier = <TEnum extends Enum>(enums: TEnum[]) => {
  assert(isEnums(enums));
  return (_: Editable) => {
    assert(isEditable(_));
    assert(!isEmptyEditable(_));
    return editable_TRY_TO.enumIdentifier(enums)(_) !== undefined;
  }
}
const editable_IS_id = (_: Editable) => {
  assert(isEditable(_));
  assert(!isEmptyEditable(_));
  return editable_TRY_TO.id(_) !== undefined;
}
const editable_IS_percentage = (_: Editable) => {
  assert(isEditable(_));
  assert(!isEmptyEditable(_));
  return editable_TRY_TO.percentage(_) !== undefined;
}

export const editable_IS = {
  text:           editable_IS_text,
  dateOnly:       editable_IS_dateOnly,
  dateTime:       editable_IS_dateTime,
  int:            editable_IS_int,
  decimal:        editable_IS_decimal,
  money:          editable_IS_money,
  enum:           editable_IS_enum,
  enumIdentifier: editable_IS_enumIdentifier,
  id:             editable_IS_id,
  percentage:     editable_IS_percentage,
}

// #endregion
// #region editable IS OPTIONAL

const editable_IS_OPTIONAL_text = (_: Editable) => {
  assert(isEditable(_));
  return isEmptyEditable(_) || editable_IS.text(_);
}
const editable_IS_OPTIONAL_dateOnly = (_: Editable) => {
  assert(isEditable(_));
  return isEmptyEditable(_) || editable_IS.dateOnly(_);
}
const editable_IS_OPTIONAL_dateTime = (_: Editable) => {
  assert(isEditable(_));
  return isEmptyEditable(_) || editable_IS.dateTime(_);
}
const editable_IS_OPTIONAL_int = (_: Editable) => {
  assert(isEditable(_));
  return isEmptyEditable(_) || editable_IS.int(_);
}
const editable_IS_OPTIONAL_decimal = (fractionDigits: number) => {
  assert(isFractionDigits(fractionDigits));
  return (_: Editable) => {
    assert(isEditable(_));
    return isEmptyEditable(_) || editable_IS.decimal(fractionDigits)(_);
  }
}
const editable_IS_OPTIONAL_money = (_: Editable) => {
  assert(isEditable(_));
  return isEmptyEditable(_) || editable_IS.money(_);
}
const editable_IS_OPTIONAL_enum = <TEnum extends Enum>(enums: TEnum[]) => {
  assert(isEnums(enums));
  return (_: Editable) => {
    assert(isEditable(_));
    return isEmptyEditable(_) || editable_IS.enum(enums)(_);
  }
}
const editable_IS_OPTIONAL_enumIdentifier = <TEnum extends Enum>(enums: TEnum[]) => {
  assert(isEnums(enums));
  return (_: Editable) => {
    assert(isEditable(_));
    return isEmptyEditable(_) || editable_IS.enumIdentifier(enums)(_);
  }
}

const editable_IS_OPTIONAL_percentage = (_: Editable) => {
  assert(isEditable(_));
  return isEmptyEditable(_) || editable_IS.percentage(_);
}

export const editable_IS_OPTIONAL = {
  text:           editable_IS_OPTIONAL_text,
  dateOnly:       editable_IS_OPTIONAL_dateOnly,
  dateTime:       editable_IS_OPTIONAL_dateTime,
  int:            editable_IS_OPTIONAL_int,
  decimal:        editable_IS_OPTIONAL_decimal,
  money:          editable_IS_OPTIONAL_money,
  enum:           editable_IS_OPTIONAL_enum,
  enumIdentifier: editable_IS_OPTIONAL_enumIdentifier,
  percentage: editable_IS_OPTIONAL_percentage,
}

// #endregion
// #region editable TO

const editable_TO_text = (_: Editable) => {
  assert(isEditable(_));
  assert(editable_IS.text(_));
  return editable_TRY_TO.text(_)!;
}
const editable_TO_dateOnly = (_: Editable) => {
  assert(isEditable(_));
  assert(editable_IS.dateOnly(_));
  return editable_TRY_TO.dateOnly(_)!;
}
const editable_TO_dateTime = (_: Editable) => {
  assert(isEditable(_));
  assert(editable_IS.dateTime(_));
  return editable_TRY_TO.dateTime(_)!;
}
const editable_TO_int = (_: Editable) => {
  assert(isEditable(_));
  assert(editable_IS.int(_));
  return editable_TRY_TO.int(_)!;
}
const editable_TO_decimal = (fractionDigits: number) => {
  assert(isFractionDigits(fractionDigits));
  return (_: Editable) => {
    assert(isEditable(_));
    assert(editable_IS.decimal(fractionDigits)(_));
    return editable_TRY_TO.decimal(fractionDigits)(_)!;
  }
}
const editable_TO_money = (_: Editable) => {
  assert(isEditable(_));
  assert(editable_IS.money(_));
  return editable_TRY_TO.money(_)!;
}
const editable_TO_enum = <TEnum extends Enum>(enums: TEnum[]) => {
  assert(isEnums(enums));
  return (_: Editable) => {
    assert(isEditable(_));
    assert(editable_IS.enum(enums)(_));
    return editable_TRY_TO.enum(enums)(_)!;
  }
}
const editable_TO_enumIdentifier = <TEnum extends Enum>(enums: TEnum[]) => {
  assert(isEnums(enums));
  return (_: Editable) => {
    assert(isEditable(_));
    assert(editable_IS.enumIdentifier(enums)(_));
    return editable_TRY_TO.enumIdentifier(enums)(_)!;
  }
}
const editable_TO_id = (_: Editable) => {
  assert(isEditable(_));
  assert(editable_IS.id(_));
  return editable_TRY_TO.id(_);
}
const editable_TO_percentage = (_: Editable) => {
  assert(isEditable(_));
  assert(editable_IS.percentage(_));
  return editable_TRY_TO.percentage(_)!;
}

export const editable_TO = {
  text:           editable_TO_text,
  dateOnly:       editable_TO_dateOnly,
  dateTime:       editable_TO_dateTime,
  int:            editable_TO_int,
  decimal:        editable_TO_decimal,
  money:          editable_TO_money,
  enum:           editable_TO_enum,
  enumIdentifier: editable_TO_enumIdentifier,
  id:             editable_TO_id,
  percentage:     editable_TO_percentage,
}

// #endregion
// #region editable TO OPTIONAL

const editable_TO_OPTIONAL_text = (_: Editable) => {
  assert(isEditable(_));
  return isEmptyEditable(_) ? undefined : editable_TO.text(_);
}
const editable_TO_OPTIONAL_dateOnly = (_: Editable) => {
  assert(isEditable(_));
  return isEmptyEditable(_) ? undefined : editable_TO.dateOnly(_);
}
const editable_TO_OPTIONAL_dateTime = (_: Editable) => {
  assert(isEditable(_));
  return isEmptyEditable(_) ? undefined : editable_TO.dateTime(_);
}
const editable_TO_OPTIONAL_int = (_: Editable) => {
  assert(isEditable(_));
  return isEmptyEditable(_) ? undefined : editable_TO.int(_);
}
const editable_TO_OPTIONAL_decimal = (fractionDigits: number) => {
  assert(isFractionDigits(fractionDigits));
  return (_: Editable) => {
    assert(isEditable(_));
    return isEmptyEditable(_) ? undefined : editable_TO.decimal(fractionDigits)(_);
  }
}
const editable_TO_OPTIONAL_money = (_: Editable) => {
  assert(isEditable(_));
  return isEmptyEditable(_) ? undefined : editable_TO.money(_);
}
const editable_TO_OPTIONAL_enum = <TEnum extends Enum>(enums: TEnum[]) => {
  assert(isEnums(enums));
  return (_: Editable) => {
    assert(isEditable(_));
    return isEmptyEditable(_) ? undefined : editable_TO.enum(enums)(_);
  }
}
const editable_TO_OPTIONAL_enumIdentifier = <TEnum extends Enum>(enums: TEnum[]) => {
  assert(isEnums(enums));
  return (_: Editable) => {
    assert(isEditable(_));
    return isEmptyEditable(_) ? undefined : editable_TO.enumIdentifier(enums)(_);
  }
}
const editable_TO_OPTIONAL_id = (_: Editable) => {
  assert(isEditable(_));
  return isEmptyEditable(_) ? undefined : editable_TO.id(_);
}
const editable_TO_OPTIONAL_percentage = (_: Editable) => {
  assert(isEditable(_));
  return isEmptyEditable(_) ? undefined : editable_TO.percentage(_);
}

export const editable_TO_OPTIONAL = {
  text:           editable_TO_OPTIONAL_text,
  dateOnly:       editable_TO_OPTIONAL_dateOnly,
  dateTime:       editable_TO_OPTIONAL_dateTime,
  int:            editable_TO_OPTIONAL_int,
  decimal:        editable_TO_OPTIONAL_decimal,
  money:          editable_TO_OPTIONAL_money,
  enum:           editable_TO_OPTIONAL_enum,
  enumIdentifier: editable_TO_OPTIONAL_enumIdentifier,
  id:             editable_TO_OPTIONAL_id,
  percentage:     editable_TO_OPTIONAL_percentage,
}

// #endregion

// #region NORMALIZE

const NORMALIZE_string = (_: Editable): Editable => {
  assert(isEditable(_));
  assert(editable_IS.text(_));
  return editable_FROM.text(editable_TO.text(_));
}
const NORMALIZE_dateOnly = (_: Editable): Editable => {
  assert(isEditable(_));
  assert(editable_IS.dateOnly(_));
  return editable_FROM.dateOnly(editable_TO.dateOnly(_));
}
const NORMALIZE_dateTime = (_: Editable): Editable => {
  assert(isEditable(_));
  assert(editable_IS.dateTime(_), `Invalid value: ${_}`);
  return editable_FROM.dateTime(editable_TO.dateTime(_));
}
const NORMALIZE_int = (_: Editable): Editable => {
  assert(isEditable(_));
  assert(editable_IS.int(_));
  return editable_FROM.int(editable_TO.int(_));
}
const NORMALIZE_decimal = (fractionDigits: number) => {
  assert(isFractionDigits(fractionDigits));
  return (_: Editable): Editable => {
    assert(isEditable(_));
    assert(editable_IS.decimal(fractionDigits)(_));
    return _;
    // const oldDecimals = decimals(_);
    // const candidate = editable_FROM.decimal(fractionDigits)(editable_TO.decimal(fractionDigits)(_));
    // return candidate;
    // return editable(replaceDecimalPart(candidate, oldDecimals));
  }
}
function decimals(_: string): string {
  const match = _.match(/(\.\d+)/);
  return match ? match[0] : '';
}
function replaceDecimalPart(numStr: string, newDecimals: string): string {
  const parts = numStr.split('.');
  return `${parts[0]}.${newDecimals}`;
}
// const DECIMALS_REGEX = /\d+\.\d*/g;
// const decimals = (_: string): string =>
// // remove all trailing decimal point zeros using regex
// // const removeTrailingZeros = (_: string) => _;
// const removeTrailingZeros = (_: string) => _.replace(/\.0+$|(\.\d*[1-9])0+$/, "$1");
const NORMALIZE_money = (_: Editable): Editable => {
  assert(isEditable(_));
  assert(editable_IS.money(_));
  return _;
  // return editable_FROM.money(editable_TO.money(_));
}
const NORMALIZE_enum = <TEnum extends Enum>(enums: TEnum[]) => {
  assert(isEnums(enums));
  return (_: Editable): Editable => {
    assert(isEditable(_));
    assert(editable_IS.enum(enums)(_));
    return editable_FROM.enum(enums)(editable_TO.enum(enums)(_));
  }
}
const NORMALIZE_enumIdentifier = <TEnum extends Enum>(enums: TEnum[]) => {
  assert(isEnums(enums));
  return (_: Editable): Editable => {
    assert(isEditable(_));
    assert(editable_IS.enumIdentifier(enums)(_));
    return editable_FROM.enumIdentifier(enums)(editable_TO.enumIdentifier(enums)(_));
  }
}

export const NORMALIZE = {
  string:         NORMALIZE_string,
  dateOnly:       NORMALIZE_dateOnly,
  dateTime:       NORMALIZE_dateTime,
  int:            NORMALIZE_int,
  decimal:        NORMALIZE_decimal,
  money:          NORMALIZE_money,
  enum:           NORMALIZE_enum,
  enumIdentifier: NORMALIZE_enumIdentifier,
}

// #endregion
// #region NORMALIZE OPTIONAL

const NORMALIZE_OPTIONAL_string = (_: Editable): Editable => {
  assert(isEditable(_));
  return isEmptyEditable(_) ? editable() : NORMALIZE.string(_);
}
const NORMALIZE_OPTIONAL_dateOnly = (_: Editable): Editable => {
  assert(isEditable(_));
  return isEmptyEditable(_) ? editable() : NORMALIZE.dateOnly(_);
}
const NORMALIZE_OPTIONAL_dateTime = (_: Editable): Editable => {
  assert(isEditable(_));
  return isEmptyEditable(_) ? editable() : NORMALIZE.dateTime(_);
}
const NORMALIZE_OPTIONAL_int = (_: Editable): Editable => {
  assert(isEditable(_));
  return isEmptyEditable(_) ? editable() : NORMALIZE.int(_);
}
const NORMALIZE_OPTIONAL_decimal = (fractionDigits: number) => {
  assert(isFractionDigits(fractionDigits));
  return (_: Editable): Editable => {
    assert(isEditable(_));
    return isEmptyEditable(_) ? editable() : NORMALIZE.decimal(fractionDigits)(_);
  }
}
const NORMALIZE_OPTIONAL_money = (_: Editable): Editable => {
  assert(isEditable(_));
  return isEmptyEditable(_) ? editable() : NORMALIZE.money(_);
}
const NORMALIZE_OPTIONAL_enum = <TEnum extends Enum>(enums: TEnum[]) => {
  assert(isEnums(enums));
  return (_: Editable): Editable => {
    assert(isEditable(_));
    return isEmptyEditable(_) ? editable() : NORMALIZE.enum(enums)(_);
  }
}
const NORMALIZE_OPTIONAL_enumIdentifier = <TEnum extends Enum>(enums: TEnum[]) => {
  assert(isEnums(enums));
  return (_: Editable): Editable => {
    assert(isEditable(_));
    return isEmptyEditable(_) ? editable() : NORMALIZE.enumIdentifier(enums)(_);
  }
}

export const NORMALIZE_OPTIONAL = {
  string:         NORMALIZE_OPTIONAL_string,
  dateOnly:       NORMALIZE_OPTIONAL_dateOnly,
  dateTime:       NORMALIZE_OPTIONAL_dateTime,
  int:            NORMALIZE_OPTIONAL_int,
  decimal:        NORMALIZE_OPTIONAL_decimal,
  money:          NORMALIZE_OPTIONAL_money,
  enum:           NORMALIZE_OPTIONAL_enum,
  enumIdentifier: NORMALIZE_OPTIONAL_enumIdentifier,
}

// #endregion

// #region LIKABLE

export type Likable = Text | DateOnly | DateTime | Int | Decimal | Money | Enum | EnumIdentifier | Id | Percentage;

export type Like<T extends Likable> = T | Editable | (() => T) | (() => Editable);

export type OptionalLike<T extends Likable> = Like<T> | undefined | (() => undefined);

// #endregion
// #region LIKEABLE TO

const LIKEABLE_TO_text = (_: Like<Text>): Text => {
  assert(_ !== undefined);
  assert(!isEditable(_) || editable_IS.text(_));

  if (isEditable(_)) return editable_TO.text(_);
  if (isFunction(_)) return LIKEABLE_TO_text(_());

  assert(!IS.text(_));
  unexpectedCodePath();
}
const LIKEABLE_TO_dateOnly = (_: Like<DateOnly>): DateOnly => {
  assert(_ !== undefined);
  assert(!isEditable(_) || editable_IS.dateOnly(_));

  if (IS.dateOnly(_)) return _;
  if (isEditable(_)) return editable_TO.dateOnly(_);
  if (isFunction(_)) return LIKEABLE_TO_dateOnly(_());

  unexpectedCodePath();
}
const LIKEABLE_TO_dateTime = (_: Like<DateTime>): DateTime => {
  assert(_ !== undefined);
  assert(!isEditable(_) || editable_IS.dateTime(_));

  if (IS.dateTime(_)) return _;
  if (isEditable(_)) return editable_TO.dateTime(_);
  if (isFunction(_)) return LIKEABLE_TO_dateTime(_());

  unexpectedCodePath();
}
const LIKEABLE_TO_int = (_: Like<Int>): Int => {
  assert(_ !== undefined);
  assert(!isEditable(_) || editable_IS.int(_));

  if (IS.int(_)) return _;
  if (isEditable(_)) return editable_TO.int(_);
  if (isFunction(_)) return LIKEABLE_TO_int(_());

  unexpectedCodePath();
}
const LIKEABLE_TO_decimal = (fractionDigits: number) => {
  assert(isFractionDigits(fractionDigits));
  return (_: Like<Decimal>): Decimal => {
    assert(_ !== undefined);
    assert(!isEditable(_) || editable_IS.decimal(fractionDigits)(_));

    if (IS.decimal(fractionDigits)(_)) return _;
    if (isEditable(_)) return editable_TO.decimal(fractionDigits)(_);
    if (isFunction(_)) return LIKEABLE_TO_decimal(fractionDigits)(_());

    unexpectedCodePath();
  }
}
const LIKEABLE_TO_money = (_: Like<Money>): Money => {
  assert(_ !== undefined);
  assert(!isEditable(_) || editable_IS.money(_));

  if (IS.money(_)) return _;
  if (isEditable(_)) return editable_TO.money(_);
  if (isFunction(_)) return LIKEABLE_TO_money(_());

  unexpectedCodePath();
}
const LIKEABLE_TO_enum = <TEnum extends Enum>(enums: TEnum[]) => {
  assert(isEnums(enums));
  return (_: Like<TEnum>): TEnum => {
    assert(_ !== undefined);
    assert(!isEditable(_) || editable_IS.enum(enums)(_));

    if (IS.enum(enums)(_)) return _;
    if (isEditable(_)) return editable_TO.enum(enums)(_);
    if (isFunction(_)) return LIKEABLE_TO_enum(enums)(_());

    unexpectedCodePath();
  }
}
const LIKEABLE_TO_enumIdentifier = <TEnum extends Enum>(enums: TEnum[]) => {
  assert(isEnums(enums));
  return (_: Like<EnumIdentifier>): EnumIdentifier => {
    assert(_ !== undefined);
    assert(!isEditable(_) || editable_IS.enumIdentifier(enums)(_));

    if (IS.enumIdentifier(enums)(_)) return _;
    if (isEditable(_)) return editable_TO.enumIdentifier(enums)(_);
    if (isFunction(_)) return LIKEABLE_TO_enumIdentifier(enums)(_());

    unexpectedCodePath();
  }
}

export const LIKEABLE_TO = {
  text:           LIKEABLE_TO_text,
  dateOnly:       LIKEABLE_TO_dateOnly,
  dateTime:       LIKEABLE_TO_dateTime,
  int:            LIKEABLE_TO_int,
  decimal:        LIKEABLE_TO_decimal,
  money:          LIKEABLE_TO_money,
  enum:           LIKEABLE_TO_enum,
  enumIdentifier: LIKEABLE_TO_enumIdentifier,
}

// #endregion
// #region LIKEABLE TO OPTIONAL

const LIKEABLE_TO_OPTIONAL_text = (_: OptionalLike<Text>): Text | undefined => {
  assert(!isEditable(_) || isEmptyEditable(_) || editable_IS.text(_));

  if (_ === undefined || isEmptyEditable(_)) return undefined;

  if (isEditable(_)) return editable_TO.text(_);
  if (isFunction(_)) return LIKEABLE_TO_OPTIONAL_text(_());

  assert(!IS.text(_));
  unexpectedCodePath();
}
const LIKEABLE_TO_OPTIONAL_dateOnly = (_: OptionalLike<DateOnly>): DateOnly | undefined => {
  assert(!isEditable(_) || isEmptyEditable(_) || editable_IS.dateOnly(_), `Invalid value: ${_}`);

  if (_ === undefined || isEmptyEditable(_)) return undefined;

  if (IS.dateOnly(_)) return _;
  if (isEditable(_)) return editable_TO.dateOnly(_);
  if (isFunction(_)) return LIKEABLE_TO_OPTIONAL_dateOnly(_());

  unexpectedCodePath();
}
const LIKEABLE_TO_OPTIONAL_dateTime = (_: OptionalLike<DateTime>): DateTime | undefined => {
  assert(!isEditable(_) || isEmptyEditable(_) || editable_IS.dateTime(_));

  if (_ === undefined || isEmptyEditable(_)) return undefined;

  if (IS.dateTime(_)) return _;
  if (isEditable(_)) return editable_TO.dateTime(_);
  if (isFunction(_)) return LIKEABLE_TO_OPTIONAL_dateTime(_());

  unexpectedCodePath();
}
const LIKEABLE_TO_OPTIONAL_int = (_: OptionalLike<Int>): Int | undefined => {
  assert(!isEditable(_) || isEmptyEditable(_) || editable_IS.int(_));

  if (_ === undefined || isEmptyEditable(_)) return undefined;

  if (IS.int(_)) return _;
  if (isEditable(_)) return editable_TO.int(_);
  if (isFunction(_)) return LIKEABLE_TO_OPTIONAL_int(_());

  unexpectedCodePath();
}
const LIKEABLE_TO_OPTIONAL_decimal = (fractionDigits: number) => {
  assert(isFractionDigits(fractionDigits));
  return (_: OptionalLike<Decimal>): Decimal | undefined => {
    assert(!isEditable(_) || isEmptyEditable(_) || editable_IS.decimal(fractionDigits)(_));

    if (_ === undefined || isEmptyEditable(_)) return undefined;

    if (IS.decimal(fractionDigits)(_)) return _;
    if (isEditable(_)) return editable_TO.decimal(fractionDigits)(_);
    if (isFunction(_)) return LIKEABLE_TO_OPTIONAL_decimal(fractionDigits)(_());

    unexpectedCodePath();
  }
}
const LIKEABLE_TO_OPTIONAL_money = (_: OptionalLike<Money>): Money | undefined => {
  assert(!isEditable(_) || isEmptyEditable(_) || editable_IS.money(_));

  if (_ === undefined || isEmptyEditable(_)) return undefined;

  if (IS.money(_)) return _;
  if (isEditable(_)) return editable_TO.money(_);
  if (isFunction(_)) return LIKEABLE_TO_OPTIONAL_money(_());

  unexpectedCodePath();
}
const LIKEABLE_TO_OPTIONAL_enum = <TEnum extends Enum>(enums: TEnum[]) => {
  assert(isEnums(enums));
  return (_: OptionalLike<TEnum>): TEnum | undefined => {
    assert(!isEditable(_) || isEmptyEditable(_) || editable_IS.enum(enums)(_));

    if (_ === undefined || isEmptyEditable(_)) return undefined;

    if (IS.enum(enums)(_)) return _;
    if (isEditable(_)) return editable_TO.enum(enums)(_);
    if (isFunction(_)) return LIKEABLE_TO_OPTIONAL_enum(enums)(_());

    unexpectedCodePath();
  }
}
const LIKEABLE_TO_OPTIONAL_enumIdentifier = <TEnum extends Enum>(enums: TEnum[]) => {
  assert(isEnums(enums));
  return (_: OptionalLike<EnumIdentifier>): EnumIdentifier | undefined => {
    assert(!isEditable(_) || isEmptyEditable(_) || editable_IS.enumIdentifier(enums)(_));

    if (_ === undefined || isEmptyEditable(_)) return undefined;

    if (IS.enumIdentifier(enums)(_)) return _;
    if (isEditable(_)) return editable_TO.enumIdentifier(enums)(_);
    if (isFunction(_)) return LIKEABLE_TO_OPTIONAL_enumIdentifier(enums)(_());

    unexpectedCodePath();
  }
}
const LIKEABLE_TO_OPTIONAL_id = (_: OptionalLike<Id>): Id | undefined => {
  assert(!isEditable(_) || isEmptyEditable(_) || editable_IS.id(_));

  if (_ === undefined || isEmptyEditable(_)) return undefined;

  if(IS.id(_)) return _;
  if (isEditable(_)) return editable_TO.id(_);
  if (isFunction(_)) return LIKEABLE_TO_OPTIONAL_id(_());

  unexpectedCodePath();
}
const LIKEABLE_TO_OPTIONAL_percentage = (_: OptionalLike<Percentage>): Percentage | undefined => {
  assert(!isEditable(_) || isEmptyEditable(_) || editable_IS.percentage(_));

  if (_ === undefined || isEmptyEditable(_)) return undefined;

  if (IS.percentage(_)) return _;
  if (isEditable(_)) return editable_TO.percentage(_);
  if (isFunction(_)) return LIKEABLE_TO_OPTIONAL_percentage(_());

  unexpectedCodePath();
}

export const LIKEABLE_TO_OPTIONAL = {
  text:           LIKEABLE_TO_OPTIONAL_text,
  dateOnly:       LIKEABLE_TO_OPTIONAL_dateOnly,
  dateTime:       LIKEABLE_TO_OPTIONAL_dateTime,
  int:            LIKEABLE_TO_OPTIONAL_int,
  decimal:        LIKEABLE_TO_OPTIONAL_decimal,
  money:          LIKEABLE_TO_OPTIONAL_money,
  enum:           LIKEABLE_TO_OPTIONAL_enum,
  enumIdentifier: LIKEABLE_TO_OPTIONAL_enumIdentifier,
  id:             LIKEABLE_TO_OPTIONAL_id,
  percentage:     LIKEABLE_TO_OPTIONAL_percentage
}

// #endregion

// #region ARE EQUAL

const DATE_COMPARE_THRESHOLD__IN_MILLISECONDS = 2;

const ARE_EQUAL_text = (a: OptionalLike<Text>, b: OptionalLike<Text>) => {
  const aText = LIKEABLE_TO_OPTIONAL.text(a);
  const bText = LIKEABLE_TO_OPTIONAL.text(b);
  return aText === bText;
}
const ARE_EQUAL_dateOnly = (a: OptionalLike<DateOnly>, b: OptionalLike<DateOnly>) => {
  const aDateOnly = LIKEABLE_TO_OPTIONAL.dateOnly(a);
  const bDateOnly = LIKEABLE_TO_OPTIONAL.dateOnly(b);
  return aDateOnly?.getTime() === bDateOnly?.getTime();
}
const ARE_EQUAL_dateTime = (a: OptionalLike<DateTime>, b: OptionalLike<DateTime>) => {
  const aDateTime = LIKEABLE_TO_OPTIONAL.dateTime(a);
  const bDateTime = LIKEABLE_TO_OPTIONAL.dateTime(b);

  if (aDateTime === undefined && bDateTime === undefined) return true;
  if (aDateTime === undefined || bDateTime === undefined) return false;

  return Math.abs(aDateTime.getTime() - bDateTime.getTime()) < DATE_COMPARE_THRESHOLD__IN_MILLISECONDS;
}
const ARE_EQUAL_int = (a: OptionalLike<Int>, b: OptionalLike<Int>) => {
  const aInt = LIKEABLE_TO_OPTIONAL.int(a);
  const bInt = LIKEABLE_TO_OPTIONAL.int(b);
  return aInt  === bInt;
}
const ARE_EQUAL_decimal = (fractionDigits: number) => {
  assert(isFractionDigits(fractionDigits));
  return (a: OptionalLike<Decimal>, b: OptionalLike<Decimal>) => {
    const aDecimal = LIKEABLE_TO_OPTIONAL.decimal(fractionDigits)(a);
    const bDecimal = LIKEABLE_TO_OPTIONAL.decimal(fractionDigits)(b);
    return aDecimal === bDecimal
  }
}
const ARE_EQUAL_money = (a: OptionalLike<Money>, b: OptionalLike<Money>) => {
  const aMoney = LIKEABLE_TO_OPTIONAL.money(a);
  const bMoney = LIKEABLE_TO_OPTIONAL.money(b);
  return aMoney === bMoney
}
const ARE_EQUAL_enum = <TEnum extends Enum>(enums: TEnum[]) => {
  assert(isEnums(enums));
  return (a: OptionalLike<TEnum>, b: OptionalLike<TEnum>) => {
    const aEnum = LIKEABLE_TO_OPTIONAL.enum(enums)(a);
    const bEnum = LIKEABLE_TO_OPTIONAL.enum(enums)(b);
    return aEnum?.identifier === bEnum?.identifier
  }
}
const ARE_EQUAL_enumIdentifier = <TEnum extends Enum>(enums: TEnum[]) => {
  assert(isEnums(enums));
  return (a: OptionalLike<EnumIdentifier>, b: OptionalLike<EnumIdentifier>) => {
    const aEnumIdentifier = LIKEABLE_TO_OPTIONAL.enumIdentifier(enums)(a);
    const bEnumIdentifier = LIKEABLE_TO_OPTIONAL.enumIdentifier(enums)(b);
    return aEnumIdentifier === bEnumIdentifier
  }
}
const ARE_EQUAL_id = (a: OptionalLike<Id>, b: OptionalLike<Id>) => {
  const aId = LIKEABLE_TO_OPTIONAL.id(a);
  const bId = LIKEABLE_TO_OPTIONAL.id(b);

  if (aId === undefined && bId === undefined) return true;
  if (aId === undefined || bId === undefined) return false;

  return aId?.asStr === bId?.asStr;
}
const ARE_EQUAL_percentage = (a: OptionalLike<Percentage>, b: OptionalLike<Percentage>) => {
  const aPercentage = LIKEABLE_TO_OPTIONAL.percentage(a);
  const bPercentage = LIKEABLE_TO_OPTIONAL.percentage(b);
  return aPercentage === bPercentage;
}

export const ARE_EQUAL = {
  text:           ARE_EQUAL_text,
  dateOnly:       ARE_EQUAL_dateOnly,
  dateTime:       ARE_EQUAL_dateTime,
  int:            ARE_EQUAL_int,
  decimal:        ARE_EQUAL_decimal,
  money:          ARE_EQUAL_money,
  enum:           ARE_EQUAL_enum,
  enumIdentifier: ARE_EQUAL_enumIdentifier,
  id:             ARE_EQUAL_id,
  percentage:     ARE_EQUAL_percentage,
}

// #endregion

// #region Local Helpers

export const isFractionDigits  = (_: any) => isInt(_) && _ >= 0 && _ <= 20;

const hasFractionDigits = (fractionDigits: number) => (_: number) => isNumber(_) && fractionalDigitsCount(_) <= fractionDigits;

const isEnums = <TEnum extends Enum>(enums: TEnum[]) => {
  if (enums === undefined) return false;
  if (enums === null     ) return false;
  if (enums.length === 0 ) return false;

  return enums.every(_ => _ !== undefined);
}
const tryDateOnly = (_: string) => {
  try   { return dateOnly_FROM_date(new Date(_)); }
  catch { return undefined;   }
}
const tryDate = (_: string) => {
  try   { return new Date(_); }
  catch { return undefined;   }
}
const tryNumber = (_: string) => {
  try   { return Number(_); }
  catch { return undefined; }
}
function fractionalDigitsCount(_: number): number {
  const numberAsString = _.toString();
  const decimalPointIndex = numberAsString.indexOf('.');
  return decimalPointIndex >= 0 ? numberAsString.length - decimalPointIndex - 1 : 0;
}

// #endregion
